// ============================================================================
// Caja de contingencia: el modelo (modo contingencia, paso 3c · oct-2026)
//
// Lo que el cajero hace sin internet queda como un PedidoContingencia guardado
// en el PC (IndexedDB). El paso 3d lo sube con `operacionesDeSubida`: cada
// operación lleva su llave (Idempotency-Key) y su hora original (X-Hecho-En),
// así un reintento nunca duplica y todo queda con la hora en que pasó.
// El motor de esa subida está en subida-contingencia.ts.
//
// Reglas:
//   - Las llaves se crean AL HACER el cambio, no al subirlo: si la subida se
//     corta a mitad, el reintento usa las mismas.
//   - Los platos que ya estaban en el servidor (pedido abierto antes del corte)
//     son de solo lectura: quitarlos sin red no se puede deshacer bien.
//   - Un pedido cobrado ya no se toca.
//   - Una línea que ya se mandó al servidor (aunque no llegara la respuesta)
//     queda congelada: no se quita ni se le suman unidades. Así un reintento
//     manda siempre el mismo cuerpo con la misma llave. Un toque más sobre ese
//     plato abre una línea nueva con su propia llave (paso 3d).
//   - Los precios son los de la copia local, y se mandan explícitos al subir:
//     el servidor cobra lo mismo que se le cobró al cliente.
//
// Sin React ni IndexedDB: se prueba con node (caja-contingencia.test.ts).
// ============================================================================
import { pisoPropina } from './moneda.ts';

/** Algo que el cajero puede entender y corregir (no una falla del almacén local). */
export class ErrorCaja extends Error {
  constructor(mensaje: string) {
    super(mensaje);
    this.name = 'ErrorCaja';
  }
}

export const METODOS_CONTINGENCIA = {
  efectivo: { metodo: 'Efectivo', dian: '10' },
  datafono: { metodo: 'Datáfono Redeban', dian: '48' },
} as const;
export type MetodoContingencia = keyof typeof METODOS_CONTINGENCIA;

export interface LineaLocal {
  id: string;
  productoId: number;
  codigo: string;
  nombre: string;
  /** Precio de carta (con impuesto incluido), de la copia local. */
  precio: number;
  cantidad: number;
  /** Hora original del primer toque (ISO con zona). */
  hechoEn: string;
  /** Llave de la subida de esta línea. */
  llave: string;
  /** Ya se mandó al servidor (o se intentó): no se toca más (paso 3d). */
  enviada?: boolean;
}

/** Línea que ya estaba en el servidor antes del corte. Solo lectura. */
export interface LineaServidor {
  id: number | string;
  nombre: string;
  cantidad: number;
  subtotal: number;
}

export interface PagoLocal {
  metodo: MetodoContingencia;
  monto: number;
  /** Datáfono: número de aprobación del voucher (obligatorio). */
  voucher?: string;
  /** Efectivo: lo que entregó el cliente (para el cambio). */
  recibido?: number;
}

export interface CobroLocal {
  hechoEn: string;
  llaveFacturar: string;
  llavePagar: string;
  propina: number;
  pagos: PagoLocal[];
  /** Si el cliente pidió factura electrónica: llega cuando vuelva internet. */
  correoFE?: string;
}

export type DestinoPedido =
  | { tipo: 'mesa'; mesaId: number; mesaNombre: string; grupoId: number | null }
  | { tipo: 'para_llevar'; etiqueta: string };

/** 'subiendo' quedó del paso 3c y no se escribe: lo que se está subiendo se sabe en memoria. */
export type EstadoPedidoLocal = 'abierto' | 'cobrado' | 'subiendo' | 'subido' | 'requiere_revision';

/** Lo que el servidor ya confirmó de este pedido (paso 3d). */
export interface RegistroSubida {
  /** Llaves de las operaciones que el servidor confirmó. */
  hechas: string[];
  /** Id del pedido en el servidor (el que ya existía, o el que devolvió crear). */
  pedidoId?: number;
  referencia?: string;
  /** Prefijo y folio de la venta, cuando se facturó. */
  factura?: string;
  /** Cuándo quedó todo en el servidor. */
  terminadoEn?: string;
  /** Cuándo se frenó por última vez. */
  revisionEn?: string;
}

export interface PedidoContingencia {
  id: string;
  version: 1;
  sedeId: number;
  usuarioId: number;
  creadoEn: string;
  destino: DestinoPedido;
  /** Pedido que ya existía en el servidor (de la copia local), o null si es nuevo. */
  servidor: {
    pedidoId: number;
    referencia: string;
    lineas: LineaServidor[];
    /** Total del pedido en el servidor (si ya está facturado, incluye propina). */
    total: number;
    facturado: boolean;
  } | null;
  /** Llave para crear el pedido en el servidor (solo si es nuevo). */
  llaveCrear: string;
  lineas: LineaLocal[];
  cobro: CobroLocal | null;
  estado: EstadoPedidoLocal;
  /** Lo que respondió el servidor cuando se frenó la subida. */
  error?: string;
  /** En qué operación se frenó. */
  errorEn?: OperacionSubida['que'];
  subida?: RegistroSubida;
}

// ---------------------------------------------------------------------------
// Crear y editar
// ---------------------------------------------------------------------------
export function nuevoPedido(a: {
  id: string;
  sedeId: number;
  usuarioId: number;
  destino: DestinoPedido;
  servidor: PedidoContingencia['servidor'];
  ahora: string;
  llave: string;
}): PedidoContingencia {
  return {
    id: a.id, version: 1, sedeId: a.sedeId, usuarioId: a.usuarioId,
    creadoEn: a.ahora, destino: a.destino, servidor: a.servidor,
    llaveCrear: a.llave, lineas: [], cobro: null, estado: 'abierto',
  };
}

/** "Mesa 5" o "Para llevar 2". */
export function nombreDestino(p: PedidoContingencia): string {
  return p.destino.tipo === 'mesa' ? p.destino.mesaNombre : p.destino.etiqueta;
}

export function editable(p: PedidoContingencia): boolean {
  return p.estado === 'abierto' && !p.cobro && !p.servidor?.facturado;
}

/** Por qué no se puede tocar, en palabras del cajero. */
export function motivoNoEditable(p: PedidoContingencia): string {
  if (p.estado === 'subido') return p.cobro
    ? 'Este pedido ya se cobró y está en el sistema.'
    : 'Este pedido ya está en el sistema: síguelo en la pantalla de siempre.';
  if (p.estado === 'requiere_revision') return 'Este pedido requiere revisión: avisa al administrador.';
  if (p.cobro || p.estado !== 'abierto') return 'Este pedido ya se cobró.';
  if (p.servidor?.facturado) return 'Este pedido ya estaba facturado: solo falta cobrarlo.';
  return 'Este pedido ya no se puede modificar.';
}

/** Un toque más sobre un plato: suma a su línea local o abre una nueva. */
export function agregarPlato(
  p: PedidoContingencia,
  prod: { id: number; codigo: string; nombre: string; precio: number },
  a: { ahora: string; id: string; llave: string },
): PedidoContingencia {
  if (!editable(p)) throw new ErrorCaja(motivoNoEditable(p));
  const precio = Number(prod.precio);
  if (!(precio >= 0)) throw new ErrorCaja(`"${prod.nombre}" no tiene precio en la copia local.`);
  // Solo se suma a una línea que todavía no se mandó: la mandada ya tiene su cuerpo fijo.
  const existente = p.lineas.find(l => l.productoId === prod.id && l.precio === precio && !l.enviada);
  const lineas = existente
    ? p.lineas.map(l => (l === existente ? { ...l, cantidad: l.cantidad + 1 } : l))
    : [...p.lineas, {
        id: a.id, productoId: prod.id, codigo: prod.codigo, nombre: prod.nombre,
        precio, cantidad: 1, hechoEn: a.ahora, llave: a.llave,
      }];
  return { ...p, lineas };
}

/** Quita una unidad de una línea local; con la última, la línea desaparece. */
export function quitarUno(p: PedidoContingencia, lineaId: string): PedidoContingencia {
  if (!editable(p)) throw new ErrorCaja(motivoNoEditable(p));
  if (p.lineas.find(l => l.id === lineaId)?.enviada) {
    throw new ErrorCaja('Ese plato ya se mandó al sistema: no se puede quitar desde la caja.');
  }
  const lineas = p.lineas
    .map(l => (l.id === lineaId ? { ...l, cantidad: l.cantidad - 1 } : l))
    .filter(l => l.cantidad > 0);
  return { ...p, lineas };
}

// ---------------------------------------------------------------------------
// Totales
// ---------------------------------------------------------------------------
const r2 = (x: number) => Math.round(x * 100) / 100;

export function consumo(p: PedidoContingencia): number {
  const servidor = p.servidor && !p.servidor.facturado
    ? p.servidor.lineas.reduce((s, l) => s + Number(l.subtotal || 0), 0)
    : 0;
  const local = p.lineas.reduce((s, l) => s + l.precio * l.cantidad, 0);
  return r2(servidor + local);
}

/** Propina sugerida: % sobre el consumo, hacia abajo al múltiplo de 100 (como en caja). */
export function propinaPorcentaje(consumoBruto: number, pct: number): number {
  return pisoPropina(consumoBruto * pct / 100);
}

/** Lo que hay que cobrar. Si ya estaba facturado, el total del servidor manda. */
export function totalACobrar(p: PedidoContingencia, propina: number): number {
  if (p.servidor?.facturado) return r2(p.servidor.total);
  return r2(consumo(p) + propina);
}

// ---------------------------------------------------------------------------
// Cobro
// ---------------------------------------------------------------------------
/** Devuelve el problema en palabras del cajero, o null si se puede cobrar. */
export function problemaDeCobro(p: PedidoContingencia, cobro: Omit<CobroLocal, 'hechoEn' | 'llaveFacturar' | 'llavePagar'>): string | null {
  if (p.estado !== 'abierto' || p.cobro) return motivoNoEditable(p);
  const hayLineas = p.lineas.length > 0 || (p.servidor?.lineas.length ?? 0) > 0;
  if (!hayLineas) return 'El pedido no tiene platos.';
  if (!(cobro.propina >= 0)) return 'La propina no puede ser negativa.';
  if (cobro.pagos.length === 0) return 'Falta el pago.';
  const total = totalACobrar(p, p.servidor?.facturado ? 0 : cobro.propina);
  const suma = r2(cobro.pagos.reduce((s, x) => s + Number(x.monto || 0), 0));
  if (Math.abs(suma - total) > 0.5) return `Los pagos suman ${suma} y el total es ${total}.`;
  for (const pg of cobro.pagos) {
    if (!(pg.monto > 0)) return 'Cada pago tiene que ser mayor que cero.';
    if (pg.metodo === 'datafono' && !String(pg.voucher || '').trim()) {
      return 'Falta el número de aprobación del datáfono.';
    }
    if (pg.metodo === 'efectivo' && pg.recibido != null && pg.recibido < pg.monto) {
      return 'El efectivo recibido no alcanza.';
    }
  }
  if (cobro.correoFE && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cobro.correoFE.trim())) {
    return 'El correo para la factura electrónica no es válido.';
  }
  return null;
}

export function cobrar(
  p: PedidoContingencia,
  cobro: Omit<CobroLocal, 'hechoEn' | 'llaveFacturar' | 'llavePagar'>,
  a: { ahora: string; llaveFacturar: string; llavePagar: string },
): PedidoContingencia {
  const problema = problemaDeCobro(p, cobro);
  if (problema) throw new ErrorCaja(problema);
  const correo = cobro.correoFE?.trim() || undefined;
  return {
    ...p,
    estado: 'cobrado',
    cobro: {
      ...cobro,
      propina: p.servidor?.facturado ? 0 : cobro.propina,
      correoFE: correo,
      hechoEn: a.ahora, llaveFacturar: a.llaveFacturar, llavePagar: a.llavePagar,
    },
  };
}

export function cambioEfectivo(pagos: PagoLocal[]): number {
  return r2(pagos.reduce((s, x) => s + (x.metodo === 'efectivo' && x.recibido != null ? Math.max(0, x.recibido - x.monto) : 0), 0));
}

// ---------------------------------------------------------------------------
// Subida (la ejecuta subida-contingencia.ts, paso 3d)
// ---------------------------------------------------------------------------
export interface OperacionSubida {
  /** Para mostrar y depurar: qué es. */
  que: 'crear' | 'agregar' | 'facturar' | 'pagar';
  metodo: 'POST';
  /** Ruta con ':pedido' donde va el id del servidor (se conoce tras crear). */
  ruta: string;
  llave: string;
  hechoEn: string;
  cuerpo: unknown;
}

function itemDe(l: LineaLocal) {
  return { producto_id: l.productoId, codigo: l.codigo, nombre: l.nombre, cantidad: l.cantidad, precio_unitario: l.precio };
}

/**
 * Las operaciones para dejar en el servidor lo que pasó, en orden. Cada línea
 * va sola con su hora. Si el pedido es nuevo, la primera línea lo crea.
 */
export function operacionesDeSubida(p: PedidoContingencia): OperacionSubida[] {
  const ops: OperacionSubida[] = [];
  const lineas = [...p.lineas].sort((a, b) => a.hechoEn.localeCompare(b.hechoEn));
  let resto = lineas;
  if (!p.servidor) {
    const [primera, ...otras] = lineas;
    if (!primera) return ops; // pedido nuevo sin platos: no hay nada que subir
    const d = p.destino;
    ops.push({
      que: 'crear', metodo: 'POST', ruta: '/api/pedidos', llave: p.llaveCrear, hechoEn: primera.hechoEn,
      cuerpo: {
        sede_id: p.sedeId,
        tipo: d.tipo,
        // Atendió quien lo cargó en la caja, aunque al subir haya otra sesión.
        mesero_id: p.usuarioId,
        // Igual que el POS: la mesa, y el grupo si está unida a otras.
        ...(d.tipo === 'mesa'
          ? { mesa_id: d.mesaId, ...(d.grupoId ? { grupo_id: d.grupoId } : {}) }
          : { cliente_nombre: d.etiqueta }),
        notas: 'Caja de contingencia',
        items: [itemDe(primera)],
      },
    });
    resto = otras;
  }
  for (const l of resto) {
    ops.push({ que: 'agregar', metodo: 'POST', ruta: '/api/pedidos/:pedido/items', llave: l.llave, hechoEn: l.hechoEn, cuerpo: { items: [itemDe(l)] } });
  }
  if (p.cobro) {
    if (!p.servidor?.facturado) {
      ops.push({ que: 'facturar', metodo: 'POST', ruta: '/api/pedidos/:pedido/facturar', llave: p.cobro.llaveFacturar, hechoEn: p.cobro.hechoEn, cuerpo: { propina: p.cobro.propina } });
    }
    ops.push({
      que: 'pagar', metodo: 'POST', ruta: '/api/pedidos/:pedido/pagar', llave: p.cobro.llavePagar, hechoEn: p.cobro.hechoEn,
      cuerpo: {
        pagos: p.cobro.pagos.map(pg => ({
          metodo: METODOS_CONTINGENCIA[pg.metodo].metodo,
          medio_pago_dian: METODOS_CONTINGENCIA[pg.metodo].dian,
          monto: pg.monto,
          ...(pg.voucher ? { referencia: pg.voucher.trim() } : {}),
        })),
      },
    });
  }
  return ops;
}

/** Cuántos pedidos de este equipo todavía no están en el servidor. */
export function pendientesDeSubir(pedidos: PedidoContingencia[]): number {
  return pedidos.filter(p => p.estado !== 'subido' && (p.lineas.length > 0 || p.cobro)).length;
}

/** Tiene algo que subir y nada lo frena. Un "Requiere revisión" espera a que alguien lo reintente. */
export function necesitaSubida(p: PedidoContingencia): boolean {
  return (p.estado === 'abierto' || p.estado === 'cobrado') && (p.lineas.length > 0 || !!p.cobro);
}

/** Las operaciones que el servidor todavía no confirmó, en orden. */
export function opsPendientes(p: PedidoContingencia): OperacionSubida[] {
  const hechas = new Set(p.subida?.hechas ?? []);
  return operacionesDeSubida(p).filter(o => !hechas.has(o.llave));
}

/**
 * Antes de mandar nada: las líneas quedan congeladas. Si la respuesta se
 * pierde y el cajero sigue tocando platos, el reintento manda lo mismo que
 * el primer intento (misma llave, mismo cuerpo).
 */
export function congelar(p: PedidoContingencia): PedidoContingencia {
  if (!necesitaSubida(p) || p.lineas.every(l => l.enviada)) return p;
  return { ...p, lineas: p.lineas.map(l => (l.enviada ? l : { ...l, enviada: true })) };
}

/** El servidor confirmó una operación. */
export function registrarHecha(p: PedidoContingencia, op: OperacionSubida, respuesta: unknown): PedidoContingencia {
  const r = (respuesta ?? {}) as { id?: unknown; referencia?: unknown; prefijo?: unknown; folio?: unknown };
  const antes = p.subida ?? { hechas: [] };
  const s: RegistroSubida = {
    ...antes,
    hechas: antes.hechas.includes(op.llave) ? antes.hechas : [...antes.hechas, op.llave],
  };
  if (s.pedidoId == null && p.servidor) s.pedidoId = p.servidor.pedidoId;
  if (op.que === 'crear') {
    s.pedidoId = Number(r.id);
    if (r.referencia != null) s.referencia = String(r.referencia);
  }
  if (op.que === 'facturar' && r.folio != null) s.factura = `${r.prefijo ?? ''}${r.folio}`;
  return { ...p, subida: s };
}

/** El servidor rechazó una operación: el pedido se frena entero. */
export function marcarRevision(p: PedidoContingencia, op: OperacionSubida, mensaje: string, ahora: string): PedidoContingencia {
  return {
    ...p,
    estado: 'requiere_revision',
    error: mensaje || 'El servidor rechazó el cambio.',
    errorEn: op.que,
    subida: { ...(p.subida ?? { hechas: [] }), revisionEn: ahora },
  };
}

/**
 * Todo confirmado: 'subido'. Si no estaba cobrado, el pedido sigue abierto en
 * el servidor y desde aquí se atiende en la pantalla de siempre (una mesa
 * abierta vive en un solo equipo).
 */
export function cerrarSiCompleto(p: PedidoContingencia, ahora: string): PedidoContingencia {
  if (!necesitaSubida(p) || opsPendientes(p).length > 0) return p;
  return { ...p, estado: 'subido', subida: { ...(p.subida ?? { hechas: [] }), terminadoEn: ahora } };
}

/** "Reintentar subida": vuelve a la cola con las mismas llaves (no duplica). */
export function reintentar(p: PedidoContingencia): PedidoContingencia {
  if (p.estado !== 'requiere_revision') return p;
  const q: PedidoContingencia = { ...p, estado: p.cobro ? 'cobrado' : 'abierto' };
  delete q.error;
  delete q.errorEn;
  return q;
}

/**
 * Un pedido que se subió abierto, visto como "lo que ya está en el servidor".
 * Sirve si la red se vuelve a caer antes de que la copia local lo traiga.
 */
export function referenciaDeSubido(p: PedidoContingencia): NonNullable<PedidoContingencia['servidor']> | null {
  if (p.estado !== 'subido' || p.cobro || p.subida?.pedidoId == null) return null;
  return {
    pedidoId: p.subida.pedidoId,
    referencia: p.subida.referencia ?? p.servidor?.referencia ?? '',
    lineas: [
      ...(p.servidor?.lineas ?? []),
      ...p.lineas.map(l => ({ id: l.id, nombre: l.nombre, cantidad: l.cantidad, subtotal: r2(l.precio * l.cantidad) })),
    ],
    total: consumo(p),
    facturado: false,
  };
}
